import { Component, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiClientConstant, ApiConnector, Table } from '@cureskin/api-client';
import { addDays, differenceInDays, isAfter, startOfDay, addHours, subDays } from 'date-fns';
import { ConnectionService } from '../../../services/connection-service';
import { AppConfig } from '../../app.config';
import { LocalStorage } from '../../../services/local-storage-service';

@Component({
    selector: 'dashboard-followup',
    templateUrl: './followup.html',
    styleUrls: ['../dashboard.scss'],
    standalone: false
})
export class DashboardFollowupComponent {
  startDate: Date;
  endDate: Date;
  user: Table.User;

  // followup variables
  minFollowupsToDone: number = 35;
  currentFollowups: number = 0;
  finishedFollowups: number = 0;
  sopCompletedFollowups: number = 0;
  nonSopCompletedFollowups: number = 0;
  todayTotalApprovals: number = 0;
  diffInDays: number = 1;
  dataItems: Array<{ title: string, value: number }> = [];

  @Input('userDetails')
  set updateStartDate(userDetails: { startDate: Date, endDate: Date, user: Table.User }) {
    this.startDate = addHours(startOfDay(userDetails.startDate), 6);
    this.endDate = addHours(startOfDay(userDetails.endDate), 6);
    this.user = userDetails.user;
    this.getFollowupDetails();
  }

  constructor(private conn: ConnectionService,
    private storage: LocalStorage) {
  }

  async getCurrentFollowUps(): Promise<number> {
    return ApiConnector.count(Table.FollowUp, {
      where: {
        allocatedDoctor: this.user,
        doctorConfirmed: true,
        State: ApiClientConstant.FollowUp.State.PENDING,
        ready: true,
      },
    });
  }

  async getFinishedFollowUps(): Promise<{ total: number, sopCompleted: number, nonSopCompleted: number }> {
    const finishedFollowUps: Array<Table.FollowUp> = await this.conn.findFollowUps({
      where: {
        allocatedDoctor: this.user,
        doctorConfirmed: true,
        isAllocatedToBOT: { $ne: true },
        completedOn: {
          $gte: this.startDate,
          $lte: this.endDate,
        },
        State: ApiClientConstant.FollowUp.State.FINISHED,
      },
      include: ['regimens'],
      project: ['followUpCount', 'regimens'],
      limit: 2000,
    });
    let sopCompleted: number = 0;
    let nonSopCompleted: number = 0;
    finishedFollowUps.forEach((followUp: Table.FollowUp) => {
      const followUpCount: number = followUp.get('followUpCount');
      const regimens: Array<any> = followUp.get('regimens') || [];
      const hasSopTransition: boolean = followUpCount === 1
        && regimens.some((regimen: any) => {
          const sopTransition: Array<any> = regimen.get('followUpSopTransition') || [];
          return sopTransition.length > 0;
        });
      if (hasSopTransition) {
        sopCompleted += 1;
      } else {
        nonSopCompleted += 1;
      }
    });
    return { total: finishedFollowUps.length, sopCompleted, nonSopCompleted };
  }

  async getWFIFollowUps(): Promise<number> {
    return ApiConnector.count(Table.FollowUp, {
      where: {
        allocatedDoctor: this.user,
        State: ApiClientConstant.FollowUp.State.WAITING_FOR_IMAGE,
        capturedAt: {
          $gte: this.startDate,
          $lte: this.endDate,
        },
      },
    });
  }

  async getTwoRegimenFollowUps(): Promise<number> {
    const allReportsSentByDoctor = await this.conn.findReports({
      where: {
        doctor: this.user,
        isSecondaryComparison: false,
        createdAt: {
          $gte: this.startDate,
          $lte: this.endDate,
        },
      },
      project: ['followUp', 'type'],
      limit: 1000,
    });
    const uniqueFollowUps = {};
    let totalTwoRegimenFollowups = 0;
    allReportsSentByDoctor.forEach((report: any) => {
      const followUpId = report.get('followUp')?.id;
      uniqueFollowUps[followUpId] = (uniqueFollowUps[followUpId] || 0) + 1;
      if (uniqueFollowUps[followUpId] > 1) {
        totalTwoRegimenFollowups += 1;
      }
    });
    return totalTwoRegimenFollowups;
  }

  async getPendingFollowUps(): Promise<number> {
    const yesterdayCompleted = await ApiConnector.count(Table.FollowUp, {
      where: {
        allocatedDoctor: this.user,
        State: ApiClientConstant.FollowUp.State.FINISHED,
        completedOn: {
          $gte: addHours(startOfDay(subDays(this.startDate, 1)), 6),
          $lte: addHours(startOfDay(this.endDate), 6),
        },
      },
    });
    let pendingFollowups = this.minFollowupsToDone - yesterdayCompleted;
    if (pendingFollowups < 0) {
      pendingFollowups = 0;
    }
    return pendingFollowups;
  }

  async getFollowupDetails(): Promise<void> {
    if (!this.startDate || !this.endDate || !this.user) {
      return;
    }
    this.dataItems = [];
    if (!this.startDate || !this.endDate || !this.user) {
      return;
    }
    this.dataItems = [];
    this.diffInDays = differenceInDays(this.endDate, this.startDate);
    this.minFollowupsToDone = this.user?.get('doctorLoadConfig')?.minFollowUpAllocation
    || (this.user.get('userType')?.includes(ApiClientConstant.User.UserType.DERMATOLOGIST)
      ? ApiClientConstant.Doctor.DermatDailyLoad.maxFollowUpLoad
      : ApiClientConstant.Doctor.MbbsDailyLoad.maxFollowUpLoad);
    if (isAfter(this.startDate, this.endDate)) {
      return;
    }
    const finishedResult = await this.getFinishedFollowUps();
    this.finishedFollowups = finishedResult.total;
    this.sopCompletedFollowups = finishedResult.sopCompleted;
    this.nonSopCompletedFollowups = finishedResult.nonSopCompleted;
    this.todayTotalApprovals = this.finishedFollowups;
    const currentFollowups = await this.getCurrentFollowUps();
    const currentWaitingForImageFollowups = await this.getWFIFollowUps();
    const totalTwoRegimenFollowups = await this.getTwoRegimenFollowUps();
    const pendingFollowups = await this.getPendingFollowUps();
    this.dataItems.push(
      { title: 'SOP Completed', value: this.sopCompletedFollowups },
      { title: 'Non-SOP Completed', value: this.nonSopCompletedFollowups },
      { title: 'Two Regimen FollowUps', value: totalTwoRegimenFollowups },
      { title: 'Captured/Allocated', value: currentFollowups },
      { title: 'Old Pending', value: pendingFollowups },
      { title: 'WFI', value: currentWaitingForImageFollowups },
    );
  }
}
